import { z } from 'zod'

import type { Money } from '@/lib/money'
import { moneySchema, percentSchema, toneSchema } from '@/schemas/common'

/**
 * The v5 portfolio contract, in the app's own vocabulary.
 *
 * Wire shapes live in `@/api/schema` and are never restated here; this file
 * describes what the app holds AFTER `portfolioService`'s mappers have run, so
 * every name is camelCase and every decimal is a branded `Money`.
 *
 * Three rules the whole file turns on, all three verified against mm-be:
 *
 *  1. EVERY decimal arrives as a JSON string (`COERCE_DECIMAL_TO_STRING: True`,
 *     `config/settings/base.py`). So money and percents are `moneySchema` /
 *     `percentSchema` — never `z.number()`, never a bare `z.string()`. Only
 *     `IntegerField`s (quantities, counts, ids) are real JSON numbers.
 *  2. NOT RECORDED IS NOT ZERO. A nullable decimal arrives as JSON `null`, not
 *     `"0.00"`, and the nullability below is copied field by field from
 *     `services/portfolio/`. Widening a non-nullable field costs nothing;
 *     narrowing a nullable one turns "no price" into a total loss on screen.
 *  3. Reads are quantised to 2 dp, writes accept 10 dp. The single exception
 *     is `TradeCosts.costPricePerShare`, the one `UNIT` (10 dp) field in the
 *     whole API — so `decimalInputSchema` (10 dp, for request bodies) and the
 *     read schemas are deliberately different shapes.
 */

// ── Primitives and enumerations ──────────────────────────────────────────────

/**
 * An `YYYY-MM-DD` date, as every DRF `DateField` in the domain sends and takes.
 *
 * A string, never a `Date`: the backend compares against `date.today()` under
 * Django's `TIME_ZONE`, and round-tripping through a JS `Date` would let a
 * browser in another zone shift the day the member actually picked.
 */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date, e.g. "2026-09-18"')

/**
 * A day that exists on the calendar: `2026-02-30` and `2026-13-01` have
 * `isoDateSchema`'s shape but not the day, and mm-be's `DateField` answers
 * them 400. For a date a URL carries, where a hand-edited or stale value must
 * fall back rather than reach mm-be as a 400 that "Try again" would repeat
 * forever. A JS `Date` rolls an overflow into the next month, so the round
 * trip catches it; year 0000 is a JS date but not a Python one.
 */
export const calendarDateSchema = isoDateSchema.refine((iso) => {
  const time = Date.parse(iso)
  return !Number.isNaN(time) && new Date(time).toISOString().startsWith(iso) && iso >= '0001'
}, 'Expected a real calendar day')

/**
 * Today in Asia/Kathmandu — the only "today" the backend will agree with.
 *
 * `validate_date` on all eight write serialisers rejects `value > date.today()`
 * evaluated in the server's zone, so a client that asks its own clock can be
 * told its own today is in the future. `en-CA` is the locale whose short date
 * is already `YYYY-MM-DD`, which is the same spelling the wire wants.
 */
export const kathmanduToday = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kathmandu' }).format(new Date())

/**
 * `price_status`, exactly two values (`services/portfolio/holdings.py`:
 * `"priced" if ltp is not None else "unavailable"`). There is no third.
 *
 * `"unavailable"` means there is no current `stock_price` row for the company
 * at all — `StockPrice.ltp` is declared non-nullable, so it can never mean
 * "the row exists but the price is missing".
 */
export const priceStatusSchema = z.enum(['priced', 'unavailable'])

/**
 * `shareholder_type`. Writes are choice-constrained to these two, and
 * `_to_profile` coalesces a legacy `NULL` to `"Individual"` on the way out.
 *
 * The read side is NOT this enum alone — see `shareholderProfileSchema`. The
 * wire types the field as a bare `CharField` over a legacy column with no
 * choices, so a third value can arrive. See the BACKEND note: the field wants to
 * be a choice serialiser.
 */
export const shareholderTypeSchema = z.enum(['Individual', 'Company'])

/**
 * The seven `BUY_KINDS` a buy may be WRITTEN as.
 *
 * Reads spell `kind` as a plain string deliberately (see `buyRowSchema`): the
 * column is a `CharField` and legacy rows predate this list.
 */
export const buyKindSchema = z.enum([
  'SECONDARY',
  'IPO',
  'FPO',
  'RIGHT',
  'BONUS',
  'AUCTION',
  'MERGER',
])

/**
 * The kinds a purchase may be EDITED to: `EDITABLE_BUY_KINDS`, i.e. the seven
 * minus `BONUS`.
 *
 * A bonus lot is not a purchase with a flag — it is a `stock_buy_history` row
 * the service layer routes into a different ledger array and prices at zero. So
 * `kind: "BONUS"` on `PATCH /transactions/purchases/{id}/` is a 400, and a
 * bonus lot cannot be converted into a purchase either.
 */
export const editableBuyKindSchema = z.enum([
  'SECONDARY',
  'IPO',
  'FPO',
  'RIGHT',
  'AUCTION',
  'MERGER',
])

/**
 * The four `{group}` path segments for edit and delete, exactly as
 * `_EDIT_SERIALIZERS` / `_MODEL_BY_GROUP` spell them. Anything else is a 404
 * `NOT_FOUND` (the generic one — *not* `TRANSACTION_NOT_FOUND`).
 *
 * These are ALSO the four keys of `GET /transactions/`, which is why the group
 * is one exported union rather than a string per call site. They are NOT the
 * create-route slugs: create posts to `bonus-dividend/` and `cash-dividend/`.
 * There is no rule connecting the two spellings — `TRANSACTION_GROUP_BY_KIND`
 * in `portfolioService.ts` is the only mapping, and it is a literal table.
 */
export const transactionGroupSchema = z.enum([
  'purchases',
  'bonus_shares',
  'sells',
  'cash_dividends',
])

/**
 * A decimal a REQUEST body may carry: up to 10 decimal places.
 *
 * Deliberately not `moneySchema`. Writes are `max_digits 20, decimal_places 10`,
 * so a member may enter `505.1234567890` — and it will be accepted, stored, and
 * then read back as `"505.12"`, because every read field is quantised to 2 dp.
 * The unrounded figure survives in exactly one place, `TradeCosts.costPricePerShare`.
 * Never model a read field with this.
 */
export const decimalInputSchema = z
  .string()
  .regex(/^\d+(\.\d{1,10})?$/, 'Expected a positive decimal with at most 10 decimal places')

// ── 1. Shareholder profiles (routes 1, 2, 3, 5) ──────────────────────────────

export const shareholderProfileSchema = z.object({
  id: z.number().int(),
  /**
   * Stored split and joined back on read (`_split_name` splits on the FIRST
   * space), so a single-token name round-trips unchanged and runs of spaces
   * collapse. Never empty — `min_length 1` on the way in.
   */
  name: z.string().min(1),
  /**
   * `null` when the stored value is neither choice. One legacy profile must not
   * fail the list every portfolio screen reads, and it must not be dropped
   * either — that would hide a member's whole book. `null` claims no type: the
   * edit dialog selects neither pill and sends none until one is picked.
   */
  shareholderType: shareholderTypeSchema.nullable().catch(({ value }) => {
    console.warn('read an unknown shareholder_type as no type', value)
    return null
  }),
  /** Nullable, and never settable at create — a new profile always has `null`. */
  demat: z.string().nullable(),
  isPrimary: z.boolean(),
})

/** `DELETE /shareholders/{id}/` — HTTP **200 with a body**, not 204. */
export const deletedProfileSchema = z.object({
  id: z.number().int(),
  /**
   * Hard-coded `True` in the service; a `false` never travels. Failure arrives
   * as an error envelope instead (409 `SHAREHOLDER_HAS_TRANSACTIONS` when the
   * profile still owns rows), so the literal is the honest type.
   */
  deleted: z.literal(true),
})

// ── 2. Holdings (routes 6, 8) ────────────────────────────────────────────────

export const holdingSchema = z.object({
  /** `""` when the company row carries a null symbol — not nullable, just empty. */
  symbol: z.string(),
  companyName: z.string(),
  /**
   * `"Uncategorized"` when the company has no sector. Never `null`, never
   * dropped, and counted as one bucket in `summary.sectorCount`. An allocation
   * chart must show it rather than filter it out, or the slices stop summing.
   */
  sector: z.string().min(1),
  /** Units held. Always > 0 — a fully-sold position leaves `/holdings/`. */
  quantity: z.number().int(),
  /** Fee-inclusive weighted average cost per unit. Never null. */
  wacc: moneySchema,
  /** Both null together when no `is_last` price row exists for the company. */
  ltp: moneySchema.nullable(),
  /**
   * Independently nullable: the column itself is nullable, so a `"priced"`
   * holding can still have no previous close and therefore no day change.
   */
  previousClose: moneySchema.nullable(),
  /**
   * `quantity × full-precision wacc`, then rounded. Never null — cost is always
   * known. Do NOT recompute it from `wacc`: `wacc` is rounded to 2 dp first, so
   * `quantity × wacc !== investment` in general.
   */
  investment: moneySchema,
  /** null when `ltp` is null. **Never render this as 0** — see `priceStatus`. */
  marketValue: moneySchema.nullable(),
  /** null when `marketValue` is null. */
  unrealizedPl: moneySchema.nullable(),
  /** Additionally null when `investment` is 0 (the percent base is guarded). */
  unrealizedPlPercent: percentSchema.nullable(),
  /** Both null when EITHER `ltp` or `previousClose` is null. */
  dayChange: moneySchema.nullable(),
  /** Additionally null when `previousClose` is 0. */
  dayChangePercent: percentSchema.nullable(),
  /** Share of the PRICED portfolio value; null when unpriced, or the total is 0. */
  weightPercent: percentSchema.nullable(),
  priceStatus: priceStatusSchema,
})

/**
 * True when this row has no market price at all.
 *
 * A helper rather than an inline comparison because the two conditions a screen
 * must keep apart look alike: this one, and "priced but with no day change".
 * Gate a day-change chip on `dayChange !== null`, never on this.
 */
export const isUnpriced = (holding: Holding): boolean => holding.priceStatus === 'unavailable'

// ── 3. Summary and overview (routes 7, 8) ────────────────────────────────────

export const portfolioSummarySchema = z.object({
  /** Open positions, priced and unpriced alike. */
  holdingsCount: z.number().int(),
  totalUnits: z.number().int(),
  /** Distinct sector strings; `"Uncategorized"` counts as one. */
  sectorCount: z.number().int(),
  /** Never null — cost is known for every holding, priced or not. */
  totalInvestment: moneySchema,
  /**
   * The five value totals below cover the PRICED SUBSET only, while
   * `totalInvestment` / `holdingsCount` / `totalUnits` / `sectorCount` cover
   * every holding. Two different denominators, so
   * `totalMarketValue − totalInvestment` is a meaningless number whenever any
   * holding is unpriced — read `totalUnrealizedPl` instead of subtracting.
   * All null (not 0) when nothing is priced.
   */
  totalMarketValue: moneySchema.nullable(),
  totalUnrealizedPl: moneySchema.nullable(),
  /** Base is `totalInvestment`. */
  totalUnrealizedPlPercent: percentSchema.nullable(),
  totalDailyPl: moneySchema.nullable(),
  /** Base is YESTERDAY's value (`totalMarketValue − totalDailyPl`), not today's. */
  totalDailyPlPercent: percentSchema.nullable(),
  /**
   * `Sum("cash_dividend")`, which is `None` over an empty set — so null means
   * NONE RECORDED and renders "—", not "Rs. 0.00".
   */
  dividendReceived: moneySchema.nullable(),
  /**
   * Ranked by `unrealizedPlPercent` across PRICED rows only, so both are null
   * when nothing is priced — and with exactly one priced holding both name the
   * SAME symbol, which a screen showing two tiles has to handle.
   */
  bestPerformer: z.string().nullable(),
  worstPerformer: z.string().nullable(),
})

/**
 * `GET /overview/` — the summary and the holdings in one round trip.
 *
 * The Home card and the portfolio header both need both halves, and two
 * separate reads can disagree: they are two computations over a table that a
 * concurrent write may have moved between them.
 */
export const portfolioOverviewSchema = z.object({
  summary: portfolioSummarySchema,
  holdings: z.array(holdingSchema),
})

// ── 4. The ledger (route 9) ──────────────────────────────────────────────────

export const buyRowSchema = z.object({
  id: z.number().int(),
  symbol: z.string(),
  /**
   * The raw `stock_type` column, a `CharField` — one of `buyKindSchema`'s seven
   * for anything v5 wrote, but legacy rows may carry other strings. A plain
   * string here on purpose: never switch on it without a default.
   */
  kind: z.string(),
  quantity: z.number().int(),
  /** What the member entered, per unit. */
  rate: moneySchema,
  date: isoDateSchema,
  /**
   * `costPricePerShare × quantity`, i.e. FEE-INCLUSIVE. So
   * `totalCost !== rate × quantity` for a secondary buy — show the server's.
   */
  totalCost: moneySchema,
})

export const sellRowSchema = z.object({
  id: z.number().int(),
  symbol: z.string(),
  quantity: z.number().int(),
  price: moneySchema,
  date: isoDateSchema,
  /**
   * The sale's stored charges, tax and profit, the sell receipt's own shape (mm-be #373, branch
   * feat/web-asks-funds-portfolio). `null` for a sale with nothing stored, and until that API is
   * live (the mapper reads an absent key as `null`). Declared lazily: `sellProceedsSchema` is
   * defined with the receipts further down.
   */
  proceeds: z.lazy(() => sellProceedsSchema).nullable(),
})

/**
 * A bonus lot. NO money field at all — bonus units cost zero by definition, so
 * a cost column shows "—" or "Free", never a formatted Rs. 0.00.
 */
export const bonusRowSchema = z.object({
  id: z.number().int(),
  symbol: z.string(),
  quantity: z.number().int(),
  date: isoDateSchema,
})

export const cashDividendRowSchema = z.object({
  id: z.number().int(),
  symbol: z.string(),
  /** Gross, before the 5% withholding. */
  amount: moneySchema,
  date: isoDateSchema,
  /**
   * The 5% withheld from this payment and what reached the member, rounded per payment so the
   * rows add up to `/income/`'s totals (mm-be #374). `null` until that API is live.
   */
  tax: moneySchema.nullable(),
  net: moneySchema.nullable(),
})

/**
 * The whole ledger, unpaginated and newest-first per array.
 *
 * A single object, so the envelope's `meta` is `null` — there is no count, use
 * each array's length. All four keys are always present (the no-profile answer
 * is exactly four empty arrays).
 *
 * Ids are unique only WITHIN a table: a `sells` row and a `purchases` row can
 * share an id. Key React lists on `` `${group}-${id}` ``, never on `id`.
 */
export const transactionHistorySchema = z.object({
  /** Excludes BONUS lots — they are split out into `bonusShares`. */
  purchases: z.array(buyRowSchema),
  sells: z.array(sellRowSchema),
  bonusShares: z.array(bonusRowSchema),
  cashDividends: z.array(cashDividendRowSchema),
})

// ── 5. Profit and loss (route 10) ────────────────────────────────────────────

export const profitLossRowSchema = z.object({
  symbol: z.string(),
  sector: z.string().min(1),
  /** 0 for a fully-closed position, which still appears in this report. */
  quantity: z.number().int(),
  /** `"0.00"` for a closed position. Never null. */
  investment: moneySchema,
  /** The WACC; `"0.00"` for a closed position. Never null. */
  purchaseRate: moneySchema,
  /** Always null for a closed position. */
  ltp: moneySchema.nullable(),
  /** null when an OPEN position cannot be priced; `"0.00"` for a closed one. */
  unrealizedGain: moneySchema.nullable(),
  /** Banked profit. NEVER null — `"0.00"` when nothing was realised. */
  realizedGain: moneySchema,
  /** null exactly when `unrealizedGain` is null. */
  totalProfit: moneySchema.nullable(),
})

export const profitLossReportSchema = z.object({
  /**
   * Open positions first (biggest by market value), then closed ones in no
   * guaranteed order. A break-even closed position is SKIPPED entirely, and
   * these rows are a different set from `/holdings/` — never join them 1:1.
   */
  rows: z.array(profitLossRowSchema),
  /**
   * null ONLY when there is ≥1 open position and none of them is priced. With
   * no open positions at all it is `"0.00"`. Two empty-ish states, two values.
   */
  totalUnrealized: moneySchema.nullable(),
  /**
   * NEVER null — `"0.00"` on an empty book. Deliberately unlike
   * `summary.totalMarketValue`, which is `null` in the same state: do not write
   * one shared "is this empty?" helper across the two payloads.
   */
  totalRealized: moneySchema,
  /** null exactly when `totalUnrealized` is null. */
  totalProfit: moneySchema.nullable(),
})

// ── 6. Write receipts (routes 11, 12, 13a, 13b) ──────────────────────────────

export const tradeCostsSchema = z.object({
  /** `quantity × price`, pre-fees. */
  amount: moneySchema,
  brokerCommission: moneySchema,
  sebonFee: moneySchema,
  dpCharge: moneySchema,
  totalCharges: moneySchema,
  /** `amount + totalCharges`. */
  netAmount: moneySchema,
  /**
   * The ONE 10 dp field in the whole portfolio API, and the only place a price
   * entered at more than 2 dp survives unrounded. Every other decimal here is
   * quantised to 2 dp.
   */
  costPricePerShare: moneySchema,
})

/**
 * `POST /transactions/buy/` and — note — `POST /transactions/bonus-dividend/`,
 * which delegates to `record_buy` with `price=0, kind="BONUS"`. A bonus receipt
 * is therefore a `BuyReceipt` whose costs are all `"0.00"` and whose
 * `costPricePerShare` is `"0.0000000000"`.
 */
export const buyReceiptSchema = z.object({
  id: z.number().int(),
  symbol: z.string(),
  kind: z.string(),
  quantity: z.number().int(),
  rate: moneySchema,
  date: isoDateSchema,
  costs: tradeCostsSchema,
})

export const sellProceedsSchema = z.object({
  amount: moneySchema,
  brokerCommission: moneySchema,
  sebonFee: moneySchema,
  /** Flat Rs. 25 today, but read it rather than hard-coding it. */
  dpCharge: moneySchema,
  totalCharges: moneySchema,
  /** `quantity × WACC`. */
  costBasis: moneySchema,
  /** CAN BE NEGATIVE — `(amount − totalCharges) − costBasis`. */
  profitBeforeTax: moneySchema,
  /** `"0.00"` when `profitBeforeTax <= 0`. */
  capitalGainTax: moneySchema,
  netReceivable: moneySchema,
  /** Per-unit: `netReceivable / quantity`. */
  netSellingPrice: moneySchema,
  profit: moneySchema,
})

export const sellReceiptSchema = z.object({
  id: z.number().int(),
  symbol: z.string(),
  quantity: z.number().int(),
  rate: moneySchema,
  date: isoDateSchema,
  /** `held − quantity`, so 0 on a full exit — drives "position closed". */
  remainingUnits: z.number().int(),
  proceeds: sellProceedsSchema,
})

export const dividendReceiptSchema = z.object({
  id: z.number().int(),
  symbol: z.string(),
  amount: moneySchema,
  date: isoDateSchema,
})

// ── 7. Transaction edit / delete acks (routes 13c, 13d) ──────────────────────

/**
 * `PATCH /transactions/{group}/{id}/` answers with the ACK ONLY — not the
 * updated row. An edit runs `rebuild_position`, which replays FIFO and reprices
 * every later sell's charges, CGT and realised profit, so the caller has to
 * refetch rather than patch a row in place. `useEditTransaction` does exactly
 * that; see the invalidation map in `queries.ts`.
 */
export const updatedTransactionSchema = z.object({
  id: z.number().int(),
  group: transactionGroupSchema,
  updated: z.literal(true),
})

/**
 * `DELETE /transactions/{group}/{id}/` — HTTP **200 with this body**, not 204,
 * despite what the generated schema says. See the upstream-bug note in
 * `portfolioService.ts`.
 */
export const deletedTransactionSchema = z.object({
  id: z.number().int(),
  group: transactionGroupSchema,
  deleted: z.literal(true),
})

// ── 8. Request drafts ────────────────────────────────────────────────────────
//
// Parsed BEFORE the request, the way `reviewDraftSchema` is in the brokers
// domain: `quantity >= 1`, a non-future date and a non-empty edit body are the
// backend's own rules, and spending a round trip to be told a spinner produced
// 0 helps nobody. The backend still enforces all of them — this is a faster
// first answer, never the only check.

/** `date` must not be in the future, in the SERVER's zone. Today is allowed. */
const notFuture = (date: string) => date <= kathmanduToday()
const NOT_FUTURE = 'A transaction cannot be dated in the future.'

const pastIsoDateSchema = isoDateSchema.refine(notFuture, NOT_FUTURE)

/** `symbol` is accepted case-insensitively and echoed back canonicalised. */
const symbolInputSchema = z.string().trim().min(1).max(100)

const quantityInputSchema = z.number().int().min(1)

export const profileCreateDraftSchema = z.object({
  name: z.string().trim().min(1).max(255),
  /** Optional on the wire; the backend defaults it to `"Individual"`. */
  shareholderType: shareholderTypeSchema.optional(),
})

/**
 * All fields optional, but at least one must be present — an empty PATCH body
 * is 400 `VALIDATION_ERROR` on `fields.non_field_errors`.
 *
 * To CLEAR a demat number send `""`, not `null`: `update_profile` guards with
 * `if demat is not None`, so `null` is treated as "not sent" and is a no-op.
 */
export const profileUpdateDraftSchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    shareholderType: shareholderTypeSchema.optional(),
    demat: z.string().max(255).nullable().optional(),
  })
  .refine((draft) => Object.keys(draft).length > 0, 'Send at least one field to change.')

export const buyDraftSchema = z.object({
  symbol: symbolInputSchema,
  quantity: quantityInputSchema,
  price: decimalInputSchema,
  date: pastIsoDateSchema,
  /** Optional; the backend defaults it to `"SECONDARY"`. */
  kind: buyKindSchema.optional(),
})

export const sellDraftSchema = z.object({
  symbol: symbolInputSchema,
  quantity: quantityInputSchema,
  price: decimalInputSchema,
  date: pastIsoDateSchema,
})

export const cashDividendDraftSchema = z.object({
  symbol: symbolInputSchema,
  amount: decimalInputSchema,
  date: pastIsoDateSchema,
})

/** No price field: `record_bonus` hard-codes `price=0, kind="BONUS"`. */
export const bonusDraftSchema = z.object({
  symbol: symbolInputSchema,
  quantity: quantityInputSchema,
  date: pastIsoDateSchema,
})

/**
 * The per-group edit body, as a DISCRIMINATED UNION on `group`.
 *
 * The generated schema types all four groups with one `PatchedBuyEditRequest`
 * (an upstream OpenAPI bug — `api/v5/portfolio/views.py` picks the serialiser
 * at runtime from `_EDIT_SERIALIZERS`, which drf-spectacular cannot see), which
 * would let a caller send `price` to a cash dividend and `kind` to a bonus lot.
 * Both are silently DROPPED by DRF and then fall through to the empty-body
 * guard, so the member gets "Send at least one field to change." for a form
 * they filled in. This union makes that a compile error instead.
 *
 * `symbol` appears on no branch: it is editable on no group, and a body of only
 * `{symbol}` is a 400 rather than a symbol-specific error. Correcting a symbol
 * is a delete plus a re-add.
 *
 * `kind` on the `purchases` branch excludes `BONUS` (`editableBuyKindSchema`).
 */
export const transactionEditDraftSchema = z.discriminatedUnion('group', [
  z.object({
    group: z.literal('purchases'),
    quantity: quantityInputSchema.optional(),
    price: decimalInputSchema.optional(),
    date: pastIsoDateSchema.optional(),
    kind: editableBuyKindSchema.optional(),
  }),
  /** No price field exists for a bonus lot — its units are free. */
  z.object({
    group: z.literal('bonus_shares'),
    quantity: quantityInputSchema.optional(),
    date: pastIsoDateSchema.optional(),
  }),
  z.object({
    group: z.literal('sells'),
    quantity: quantityInputSchema.optional(),
    price: decimalInputSchema.optional(),
    date: pastIsoDateSchema.optional(),
  }),
  /** `amount`, never `price` — a cash dividend is a sum, not a per-unit rate. */
  z.object({
    group: z.literal('cash_dividends'),
    amount: decimalInputSchema.optional(),
    date: pastIsoDateSchema.optional(),
  }),
])

/**
 * True when an edit draft carries something to change.
 *
 * `group` is the discriminator, not a field being edited, so it does not count.
 * The backend rejects an otherwise-empty body with 400 `VALIDATION_ERROR` on
 * `fields.non_field_errors`; this lets the form say so without the round trip.
 */
export const hasTransactionEdits = (draft: TransactionEditDraft): boolean =>
  Object.keys(draft).some((key) => key !== 'group')

// ── 9. The Home card's derived allocation ────────────────────────────────────

export const allocationSliceSchema = z.object({
  label: z.string().min(1),
  /** Share of the book, 0-100. A number, not Money: it sizes a bar. */
  percent: z.number().min(0).max(100),
  tone: toneSchema,
})

// ── 10. Value history (route 16) ─────────────────────────────────────────────

/**
 * `?period=`, exactly these five spellings, lowercase. Anything else is a 400
 * `VALIDATION_ERROR` on `fields.period`.
 *
 * The window is `date.today() - N days` with N = 30 / 90 / 180 / 365; `all`
 * has no lower bound. There is no `5d` and no `symbol` parameter — this
 * endpoint is PORTFOLIO-level only, so a per-holding sparkline cannot be built
 * from it. That data lives on the public
 * `GET /api/v5/market/stocks/{symbol}/price-history/?range=1W`.
 */
export const valueHistoryPeriodSchema = z.enum(['1m', '3m', '6m', '1y', 'all'])

/** What the backend uses when `period` is omitted. Sent explicitly anyway. */
export const DEFAULT_VALUE_HISTORY_PERIOD = '1y' satisfies ValueHistoryPeriod

/**
 * One day on the value curve. NO field is nullable — and that is a wart, not a
 * convenience.
 *
 * `snapshots.value_history` coalesces every stored `None` to `Decimal("0")`
 * (`snap.total_value or ZERO`), so this is the ONE place in the portfolio API
 * where "not recorded" and "zero" are the same string. A `"0.00"` point is
 * therefore suspect rather than a real zero-value day: it may mean the cron
 * wrote a row it could not price.
 *
 * `drawdown_percent` is WINDOW-relative, not all-time — `peak` is initialised
 * inside the period filter — so the same calendar day reports a different
 * drawdown under `1m` than under `all`, and the first point is always `"0.00"`.
 * Never cache one period's drawdown and reuse it under another.
 *
 * Points are oldest-first, one per calendar day, and only for days a snapshot
 * row exists: weekends and holidays are absent, NOT gap-filled with the
 * previous close. A chart must plot by date, never by array index.
 */
export const snapshotPointSchema = z.object({
  date: isoDateSchema,
  /** Market value of the book that day. */
  value: moneySchema,
  invested: moneySchema,
  dayChange: moneySchema,
  dayChangePercent: percentSchema,
  /** Total return as of that day. */
  returnPercent: percentSchema,
  /** Negative or `"0.00"`; never positive. Relative to the window's peak. */
  drawdownPercent: percentSchema,
})

// ── 11. Analytics (route 17) ─────────────────────────────────────────────────

/**
 * A RATIO, not a percent. Aliased from `moneySchema` for the same reason
 * `percentSchema` is: it is a decimal that arrives as a string and must not be
 * added with `+`, so a parallel brand would buy nothing.
 *
 * The alias exists so `turnover` reads as what it is at the point it is
 * declared. `"0.53"` means 0.53×, NOT 0.53 %. Do not multiply it by 100
 * anywhere on this path — `turnover = traded / (2 × average snapshot value)`,
 * and the "÷2" already averages the buy and sell sides.
 */
export const ratioSchema = moneySchema

/**
 * The three return measures. ALL THREE ARE GENUINELY NULLABLE, and `null`
 * means "could not be computed" — never 0 %, never an error, never an omitted
 * key. `ReturnMeasures` declares all three `required` + `allow_null`, so the
 * keys always travel and always carry JSON `null` in that state.
 *
 * `null` is the NORMAL case today, not an edge case:
 *
 *   xirr      null unless the dated cash-flow series holds both an inflow and
 *             an outflow AND the bisection bracket shows a sign change. The
 *             only one of the three that can work on day one.
 *   twr       null until `save_portfolio_snapshots` has run on TWO separate
 *             days (`if len(snaps) < 2: return None`).
 *   turnover  null until it has run ONCE with a non-zero total value.
 *
 * Nothing in mm-be schedules that command (see BACKEND CHANGES NEEDED), so
 * `twr` and `turnover` are null for EVERY user until it is wired to a cron.
 *
 * The typing is the guard rail. `Money` is a branded string, so `xirr ?? 0`
 * produces `Money | 0`, which satisfies no `Money` slot and no `formatPercent`
 * call — the coalesce that would render a fabricated "0.00 %" as a real return
 * is a compile error rather than a wrong number on a screen. There is no
 * `?? 0` on this path and there must never be one.
 */
export const returnMeasuresSchema = z.object({
  /** Money-weighted annual return, %. Annualised, day-count /365. */
  xirr: percentSchema.nullable(),
  /** Time-weighted return, %. Deposit-insensitive by construction. */
  twr: percentSchema.nullable(),
  /** A ratio — `"0.53"` is 0.53×, not 53 %. Never scale it. */
  turnover: ratioSchema.nullable(),
})

/**
 * True when a return measure was actually computed.
 *
 * The only safe gate for rendering one. A falsiness test would treat a real
 * `"0.00"` return — a book that genuinely broke even — as "not enough history
 * yet", which is a different sentence and a different truth.
 */
export const isComputedMeasure = (measure: Money | null): measure is Money => measure !== null

/**
 * One sector's contribution. Declared `allow_null`, but the service seeds every
 * bucket with `[ZERO, ZERO, ZERO]`, so `dayPl` and `totalPl` are in practice
 * always a string — do NOT read `null` here as "day move unknown" the way
 * `summary.totalDailyPl` must be read.
 *
 * `weightPercent` is `null` only when the WHOLE portfolio has no priced
 * position; a single unpriced sector beside a priced one gets `"0.00"`.
 * Sorted descending by weight, nulls and zeros last.
 */
export const sectorContributionSchema = z.object({
  /** `"Uncategorized"` fallback, never null — same as `holdingSchema.sector`. */
  sector: z.string().min(1),
  weightPercent: percentSchema.nullable(),
  dayPl: moneySchema.nullable(),
  totalPl: moneySchema.nullable(),
})

/**
 * A top gainer or loser. At most five of each.
 *
 * Ranked by ABSOLUTE rupee `unrealizedPl`, NOT by percent — `unrealizedPlPercent`
 * is carried for display only. A design that ranks movers by percent must sort
 * client-side; the backend order will not match.
 *
 * Unpriced holdings are excluded, and a break-even holding (`unrealizedPl` of
 * zero) appears in NEITHER list — so "losers is non-empty whenever gainers is"
 * is false, and both are empty when nothing is priced.
 */
export const contributorSchema = z.object({
  symbol: z.string(),
  unrealizedPl: moneySchema.nullable(),
  unrealizedPlPercent: percentSchema.nullable(),
})

/**
 * Where the total return comes from — `GET /portfolio/analytics/`'s
 * `breakdown`. `unrealizedPl` and the three return figures are `null` when
 * nothing is priced; `invested`, `realizedPl` and `dividends` never are.
 * Parsed at the boundary; no screen reads it yet.
 */
export const returnBreakdownSchema = z.object({
  invested: moneySchema,
  unrealizedPl: moneySchema.nullable(),
  realizedPl: moneySchema,
  dividends: moneySchema,
  totalReturn: moneySchema.nullable(),
  totalReturnPercent: percentSchema.nullable(),
  yieldOnCostPercent: percentSchema.nullable(),
})

export const analyticsReportSchema = z.object({
  /** Always present, never null itself — only its three members are. */
  returns: returnMeasuresSchema,
  breakdown: returnBreakdownSchema,
  sectorContribution: z.array(sectorContributionSchema),
  topGainers: z.array(contributorSchema),
  topLosers: z.array(contributorSchema),
})

// ── 12. Health (route 18) ────────────────────────────────────────────────────

/** Exactly three, lowercase. `services/portfolio/health.py` defines no fourth. */
export const healthStatusSchema = z.enum(['steady', 'watch', 'act'])

/**
 * The six check keys, in the fixed order `get_health` returns them.
 *
 * Data rather than a `z.enum`, because `key` is a bare `CharField` on the wire
 * and a seventh check added upstream should reach a screen that can render it
 * generically, not fail the whole report's parse. Look a check up BY KEY —
 * the order is stable today, but indexing into the array bakes that in.
 */
export const HEALTH_CHECK_KEYS = [
  'record_quality',
  'sector_spread',
  'company_spread',
  'below_cost',
  'upcoming_dates',
  'trading_pace',
] as const

/**
 * One traffic-light check.
 *
 * `detail` is PRE-FORMATTED, unlocalised English prose with the numbers baked
 * in — `"NABIL is 42% of the book."`, `"Turnover ratio 0.53."`. There is no
 * `value`, no `threshold` and no `count` beside it, so the number cannot be
 * restyled, localised or driven into a gauge without re-deriving it from
 * `/holdings/` and duplicating the backend's thresholds. Render it verbatim;
 * see BACKEND CHANGES NEEDED.
 */
export const healthCheckSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  status: healthStatusSchema,
  detail: z.string(),
})

/**
 * ⚠️ THERE IS NO SCORE. No 0–100, no letter grade, no percentage.
 *
 * The whole scale is three integer counters over six checks, and
 * `steady + watch + act === checks.length` by construction. If a design shows a
 * numeric score the CLIENT must derive it (`steady / checks.length`) and own
 * that choice — the backend supplies nothing to read it off, and a field
 * invented here would look like a contract value to every screen downstream.
 *
 * Two distinct "nothing here" shapes, and they are NOT the same:
 *
 *   no profile at all       `{steady: 0, watch: 0, act: 0, checks: []}`
 *   empty but real profile  `{steady: 6, watch: 0, act: 0, checks: [...6]}`
 *
 * Branch on `checks.length`, never on `steady === 0`.
 *
 * Health is NOT tier-gated: `HealthView` is plain `IsAuthenticated`, so every
 * authenticated member gets all six checks and a Free/Plus split is cosmetic
 * and readable from the network tab.
 */
export const healthReportSchema = z.object({
  steady: z.number().int(),
  watch: z.number().int(),
  act: z.number().int(),
  checks: z.array(healthCheckSchema),
})

// ── 13. Income (route 19) ────────────────────────────────────────────────────

/** Exactly three, UPPERCASE — unlike every other enum in this domain. */
export const incomeKindSchema = z.enum(['CASH', 'BONUS', 'RIGHTS'])

/**
 * Cash dividends received, rolled up per company — biggest payer first.
 *
 * `gross`, `tax` and `net` are all backend values: mm-be treats the recorded
 * amount as the gross (declared) dividend, `tax` as the 5% final withholding,
 * and `net = gross − tax` reconciled to the paisa. Never recompute one from
 * the others here — if the backend ever flips the recorded amount to mean
 * "net received", only mm-be changes. There is still no eligible quantity and
 * no per-share rate on the wire.
 */
export const dividendIncomeSchema = z.object({
  symbol: z.string(),
  companyName: z.string(),
  gross: moneySchema,
  tax: moneySchema,
  net: moneySchema,
  paymentCount: z.number().int(),
  lastDate: isoDateSchema.nullable(),
})

/**
 * An announced corporate action on a company the member still holds.
 *
 * ONE `CompanyDividend` row can yield TWO entries — a `CASH` one and a `BONUS`
 * one — sharing a symbol AND a book closure date, so a React list keyed on
 * either collides. Use `upcomingIncomeKey`.
 *
 * `detail` is the same pre-formatted English as `healthCheckSchema.detail`:
 * `"Rs 12.00 per share"`, `"5.00% bonus"`, `"1:2 rights @ Rs 100.00"`. The
 * amount, the rate and the ratio exist ONLY inside that string.
 *
 * "Held" means an open position, so a fully-exited company yields nothing.
 * AGMs are counted by `health.upcoming_dates` but never listed here, so the
 * two numbers legitimately disagree — never present them as the same count.
 */
export const upcomingIncomeSchema = z.object({
  symbol: z.string(),
  companyName: z.string(),
  kind: incomeKindSchema,
  detail: z.string(),
  bookClosureDate: isoDateSchema.nullable(),
})

/** A stable React key for an upcoming action. Symbol alone collides. */
export const upcomingIncomeKey = (row: UpcomingIncome): string =>
  `${row.symbol}-${row.kind}-${row.bookClosureDate ?? 'undated'}`

export const incomeReportSchema = z.object({
  received: z.array(dividendIncomeSchema),
  /**
   * NEVER null — `"0.00"` on an empty book, where `summary.dividendReceived`
   * is `null` for the identical state. Same number, opposite empty semantics,
   * so do NOT write one shared "is this empty?" helper across the two.
   */
  totalGross: moneySchema,
  totalTax: moneySchema,
  totalNet: moneySchema,
  upcoming: z.array(upcomingIncomeSchema),
})

// ── 14. Import (routes 14, 15) ───────────────────────────────────────────────

/** The portal key `sms` is the one that carries no file, only pasted text. */
export const SMS_PORTAL = 'sms'

/**
 * A row the parser understood and will ATTEMPT to save.
 *
 * `price` is quantised to 2 dp here. What gets stored depends on how the rows
 * are saved: the multipart commit re-parses the file and feeds full precision
 * into `record_*`, while the JSON save (`importSaveDraftSchema`) stores exactly
 * the price it is sent — so saving a preview row unedited stores this 2 dp
 * figure, not the file's.
 *
 * `kind` is a plain string for the same reason `buyRowSchema.kind` is: it is a
 * `CharField`, and the parser emits labels this client should display rather
 * than switch on without a default. Every parser today emits one of
 * `IMPORT_ROW_KINDS`, which is what the JSON save will accept back.
 *
 * On the WACC report (`meroshare_wacc`) a row is NOT a trade: it is the average
 * cost the overlay will set on a scrip already held — `kind: "SECONDARY"`, the
 * held `quantity`, `price` = the report's WACC and `date` = today.
 */
export const importedRowSchema = z.object({
  symbol: z.string(),
  kind: z.string(),
  quantity: z.number().int(),
  /** null for MeroShare, which carries no prices at all, and for any unparseable one. */
  price: moneySchema.nullable(),
  date: isoDateSchema.nullable(),
  notes: z.string(),
})

/**
 * A row that will not be saved, or was attempted and failed.
 *
 * `reason` is FREE-TEXT ENGLISH with no code, and it mixes parser prose
 * (`"no matching company"`, `"unparseable date"`) with domain-error messages
 * that leak in from the commit (`"Cannot sell more units than are held."`).
 * `symbol` may be `""`, or a truncated fragment of an unparseable SMS. Render
 * both opaquely, and never parse `reason` anywhere but the mapper.
 */
export const skippedRowSchema = z.object({
  symbol: z.string(),
  reason: z.string(),
  /**
   * The row matches a trade the book already holds, so re-importing the same
   * export does not double it. The ONE sanctioned reading of `reason`, done
   * once in `mapSkippedRow` on the exact text `"already in portfolio"`: the
   * backend sends no code for it (see BACKEND CHANGES NEEDED), so if the
   * wording ever changes this turns `false` and the row degrades to an
   * ordinary "not imported" line — shown, never lost.
   */
  alreadyInPortfolio: z.boolean(),
})

/**
 * A symbol whose sells exceed its buys WITHIN the uploaded file.
 *
 * Advisory only — the commit does not refuse a disputed symbol, it attempts
 * the rows and skips whichever overflow. Worked out BEFORE the dedupe, so a buy
 * the book already holds still offsets the file's sells.
 */
export const disputedRowSchema = z.object({
  symbol: z.string(),
  buyQuantity: z.number().int(),
  sellQuantity: z.number().int(),
})

/**
 * The two `needs_detail` statuses this client knows how to act on.
 *
 * `status` is a bare `CharField` on the wire. An unknown future status is
 * DROPPED in `mapImportPreview` rather than failing the parse: a strict enum
 * here would reject the whole preview — the member could import nothing — over
 * a hint. Dropping it loses nothing, because a needs-detail entry only ever
 * describes a row that is also in `rows` or `skipped`, where it is still shown.
 * So a screen may switch on `status` exhaustively.
 */
export const NEEDS_DETAIL_STATUSES = ['no_match', 'needs_price'] as const

export const needsDetailStatusSchema = z.enum(NEEDS_DETAIL_STATUSES)

/** A company the backend suggests for a symbol it could not match. */
export const companyMatchSchema = z.object({
  symbol: z.string(),
  /** May be `""` — the company table allows a blank name. */
  name: z.string(),
})

/**
 * A row the member should finish before saving.
 *
 *   no_match     the symbol matched no company. The SAME row is also in
 *                `skipped` as "no matching company". `candidates` may be empty,
 *                and are only looked up for the first 25 distinct symbols.
 *   needs_price  a buy (secondary, IPO, FPO, right, auction) in `rows` with no
 *                price, which would otherwise save at Rs 0.
 *
 * Carries no `kind`, `price` or `notes` (see BACKEND CHANGES NEEDED), so a
 * `no_match` row rebuilt from it cannot know whether it was a buy or a sale —
 * see `importRowDraftFromNeedsDetail`.
 */
export const needsDetailRowSchema = z.object({
  symbol: z.string(),
  quantity: z.number().int(),
  date: isoDateSchema.nullable(),
  status: needsDetailStatusSchema,
  candidates: z.array(companyMatchSchema),
})

/**
 * A scrip already held that a WACC report did NOT set, with the average cost
 * the book keeps for it. Empty for every other portal.
 */
export const uncoveredRowSchema = z.object({
  symbol: z.string(),
  wacc: moneySchema,
})

/**
 * `POST /import/preview/` — 200, and it writes NOTHING.
 *
 * `rows` is already filtered: rows with no matching company, rows with no
 * parseable date and rows the book already holds have been moved into `skipped`
 * before this is returned. So a row here is one that will be attempted — not
 * one that will succeed.
 *
 * The preview carries NO TOKEN — no preview id, no draft, no server-side
 * staging. The trades are saved by posting the reviewed rows back as JSON
 * (`importSaveDraftSchema`), which the server re-screens, re-dedupes and
 * re-disputes, so no `File` has to be kept for them. Only the WACC overlay is
 * committed by uploading its file a second time (`importDraftSchema`).
 *
 * For the WACC report, `rows` are the averages to set (see `importedRowSchema`),
 * a scrip not held is in `skipped`, `uncovered` lists the held scrips the report
 * missed, and `disputes` / `needsDetail` are always empty. On an empty book
 * every row is skipped and `rows` is `[]`.
 */
export const importPreviewSchema = z.object({
  /** Echoes what was sent. */
  portal: z.string(),
  rows: z.array(importedRowSchema),
  skipped: z.array(skippedRowSchema),
  disputes: z.array(disputedRowSchema),
  needsDetail: z.array(needsDetailRowSchema),
  uncovered: z.array(uncoveredRowSchema),
})

/**
 * `POST /import/` — 201, and a 201 does not mean every row landed.
 *
 * The whole request runs in one transaction, so a crash part-way saves nothing.
 * But a row a domain rule refuses — a sale beyond what is held — rolls back only
 * its own savepoint and lands in `skipped` with the rule's message, and the rest
 * is saved. So PARTIAL SUCCESS IS THE NORMAL OUTCOME, and the status is
 * **always 201**, even when `imported` is 0 and every row was skipped.
 *
 * So there is no "the import failed" branch to write against a status code.
 * Always render `skipped` after a commit and lead with
 * "{imported} imported, {skipped.length} skipped".
 *
 * The saved rows are not returned: refetch the book (see the invalidation map
 * in `queries.ts`).
 *
 * Saving the same rows twice no longer doubles the book: a row matching a trade
 * already held on (symbol, buy / bonus / sell, date, quantity) is skipped as
 * `alreadyInPortfolio`, counted so two genuine same-day fills of the same size
 * still both land. Cash dividends are NOT deduped — sending one twice records
 * it twice.
 *
 * For the WACC overlay, `imported` counts the scrips whose average cost was set
 * and `uncovered` lists the held scrips the report missed.
 */
export const importResultSchema = z.object({
  portal: z.string(),
  /** Rows actually saved. Can be 0 on a 201. */
  imported: z.number().int(),
  skipped: z.array(skippedRowSchema),
  disputes: z.array(disputedRowSchema),
  uncovered: z.array(uncoveredRowSchema),
})

/**
 * What a caller hands `/import/preview/`, and `/import/` for the WACC overlay
 * step. Sent as MULTIPART. The trades themselves are saved as JSON instead —
 * see `importSaveDraftSchema`.
 *
 * `portal` is a bare `CharField` on the backend with NO enum validation, so an
 * unrecognised key reaches the parser registry and comes back as 400
 * `UNKNOWN_PORTAL` rather than a field error — which is why it is typed as a
 * string here rather than as a union this client would have to keep in step
 * with a registry it cannot read (there is no endpoint that lists the portals).
 *
 * `file` is required for every portal except `sms`; the backend raises that as
 * `VALIDATION_ERROR` on `fields.file`, and this refinement says so a round
 * trip earlier. `fileTwo` is read ONLY for `combined_meroshare_tms`, where the
 * argument order is counter-intuitive: `file` is the TMS **xlsx** and
 * `fileTwo` is the MeroShare **csv**.
 *
 * `sheet` names an XLSX worksheet and silently falls back to the FIRST sheet
 * when it matches nothing — there is no warning anywhere in the response.
 */
export const importDraftSchema = z
  .object({
    portal: z.string().trim().min(1),
    file: z.instanceof(File).optional(),
    fileTwo: z.instanceof(File).optional(),
    sheet: z.string().optional(),
    smsText: z.string().optional(),
  })
  .refine((draft) => draft.portal === SMS_PORTAL || draft.file !== undefined, {
    message: 'Pick a file to import.',
    path: ['file'],
  })

// ── 14b. The JSON save (route 15, `{portal, rows}`) ──────────────────────────

/**
 * The nine kinds a saved row may be: `IMPORT_ROW_KINDS` in
 * `api/v5/portfolio/serializers.py`, i.e. the seven `BUY_KINDS` plus `SELL`
 * and `DIVIDEND`. The serializer upper-cases what it is sent and refuses
 * anything else with "Unsupported transaction kind.".
 */
export const IMPORT_ROW_KINDS = [
  'SECONDARY',
  'IPO',
  'FPO',
  'RIGHT',
  'BONUS',
  'AUCTION',
  'MERGER',
  'SELL',
  'DIVIDEND',
] as const

export const importRowKindSchema = z.enum(IMPORT_ROW_KINDS)

/** `rows` is `1..MAX_IMPORT_ROWS` per request (`MAX_IMPORT_ROWS` in the serializer). */
export const MAX_IMPORT_SAVE_ROWS = 5000

/** The row fields a save can fault, in the order a row is laid out. */
export const IMPORT_ROW_FIELDS = ['symbol', 'kind', 'quantity', 'price', 'date', 'notes'] as const

export type ImportRowField = (typeof IMPORT_ROW_FIELDS)[number]

export const isImportRowField = (name: string): name is ImportRowField =>
  (IMPORT_ROW_FIELDS as readonly string[]).includes(name)

/**
 * A row as the review page holds it while the member edits it — anything its
 * controls can produce, valid or not. `importRowErrors` says what is wrong with
 * one; `importSaveDraftSchema` turns a list of valid ones into the request.
 *
 * `price` is the text of the box, `null` when it is empty — never `""`, which
 * reads as a malformed rate. For a `DIVIDEND` row it is the dividend AMOUNT
 * (`apply_cash_dividend(amount=price)`), not a per-unit rate, and `quantity` is
 * ignored; for a `BONUS` row it is ignored, since bonus units are free.
 */
export interface ImportRowDraft {
  symbol: string
  /** A plain string so a preview row's `kind` fits unconverted; checked on save. */
  kind: string
  /** `NaN` from an empty or unreadable box is refused like any other bad count. */
  quantity: number
  price: string | null
  date: string | null
  notes: string
}

/** What the review page hands `importSave`. */
export interface ImportSaveDraft {
  /**
   * The preview's own `portal`. A label for every trade portal (echoed back in
   * the result) — but a WACC-overlay portal turns the save into an overlay that
   * sets average costs instead of saving trades, so never send a trade list
   * under `meroshare_wacc`.
   */
  portal: string
  rows: readonly ImportRowDraft[]
}

/** Field-level messages for one row, keyed by the row's own field names. */
export type ImportRowErrors = Partial<Record<ImportRowField, string>>

const IMPORT_ROW_MESSAGES = {
  symbol: 'Choose a company.',
  kind: 'Choose what kind of transaction this is.',
  quantity: 'Enter the number of kitta, 1 or more.',
  price: 'Enter the rate, like 498 or 498.50.',
  dividend: 'Enter the dividend amount, more than 0.',
  date: 'Enter the date of this transaction.',
} as const

/** Did every field a cross-field rule reads parse cleanly? Only then is the rule meaningful. */
const parsedCleanly =
  (...fields: readonly ImportRowField[]) =>
  (payload: z.core.ParsePayload): boolean =>
    !payload.issues.some((issue) => fields.some((field) => issue.path?.[0] === field))

/**
 * One row of the JSON save, mirroring `ImportRowInputSerializer` rule for rule
 * — with its messages rewritten for the member, since these are shown inline
 * beside the cell rather than logged.
 *
 * ONE deliberate difference: `date` is required. The serializer accepts `null`,
 * but `imports._screen` then skips the row as "unparseable date" — a dateless
 * row can never import, whatever portal it rides under. Refusing it here says
 * so beside the cell, the same way `importDraftSchema` refuses a missing file a
 * round trip early.
 *
 * "Not in the future" is the shared `notFuture` rule, which reads the clock when
 * a row is PARSED, never when this module loads — so a review page left open
 * past midnight still agrees with the server's `date.today()`.
 *
 * The two cross-field rules run only once the fields they read have parsed
 * (`when`), so a row with a bad date AND a zero quantity reports both at once
 * instead of one per save attempt — the serializer itself reports them one at
 * a time.
 */
export const importSaveRowSchema = z
  .object({
    symbol: z
      .string({ error: IMPORT_ROW_MESSAGES.symbol })
      .trim()
      .min(1, IMPORT_ROW_MESSAGES.symbol)
      .max(100, IMPORT_ROW_MESSAGES.symbol),
    kind: z.enum(IMPORT_ROW_KINDS, { error: IMPORT_ROW_MESSAGES.kind }),
    /** `min_value=0` on the serializer; the `≥ 1` rule for all but DIVIDEND is below. */
    quantity: z
      .number({ error: IMPORT_ROW_MESSAGES.quantity })
      .int(IMPORT_ROW_MESSAGES.quantity)
      .min(0, IMPORT_ROW_MESSAGES.quantity),
    /** `decimalInputSchema`'s rule: up to 10 dp, never negative. `null` saves as Rs 0. */
    price: z
      .string({ error: IMPORT_ROW_MESSAGES.price })
      .regex(/^\d+(\.\d{1,10})?$/, IMPORT_ROW_MESSAGES.price)
      .nullable(),
    date: z
      .string({ error: IMPORT_ROW_MESSAGES.date })
      .regex(/^\d{4}-\d{2}-\d{2}$/, IMPORT_ROW_MESSAGES.date)
      .refine(notFuture, NOT_FUTURE),
    notes: z.string(),
  })
  .refine(
    // Any non-zero digit in a non-negative decimal means it is above zero —
    // decided on the text, so no float ever judges a rupee amount.
    (row) => row.kind !== 'DIVIDEND' || (row.price !== null && /[1-9]/.test(row.price)),
    {
      path: ['price'],
      message: IMPORT_ROW_MESSAGES.dividend,
      when: parsedCleanly('kind', 'price'),
    },
  )
  .refine((row) => row.kind === 'DIVIDEND' || row.quantity >= 1, {
    path: ['quantity'],
    message: IMPORT_ROW_MESSAGES.quantity,
    when: parsedCleanly('kind', 'quantity'),
  })

/**
 * The JSON save's body, `{portal, rows}`: `ImportSaveRequestSerializer`.
 *
 * The server re-screens, re-dedupes and re-disputes whatever it is sent, so an
 * edited list cannot smuggle an unknown symbol or a duplicate past it — these
 * rules only make the first answer faster, as with every other draft.
 */
export const importSaveDraftSchema = z.object({
  portal: z.string().trim().min(1),
  rows: z
    .array(importSaveRowSchema)
    .min(1, 'Keep at least one row to import.')
    .max(MAX_IMPORT_SAVE_ROWS, 'Import at most 5,000 rows at a time.'),
})

/**
 * What is wrong with one row, field by field — `{}` when it can be saved.
 *
 * The SAME rules `importSave` parses with, so a row this passes is a row the
 * save will not refuse client-side. The first message per field wins. Pure
 * apart from the "not in the future" rule, which reads today in Kathmandu.
 */
export const importRowErrors = (row: ImportRowDraft): ImportRowErrors => {
  const parsed = importSaveRowSchema.safeParse(row)
  if (parsed.success) return {}
  const errors: ImportRowErrors = {}
  for (const issue of parsed.error.issues) {
    const field = String(issue.path[0] ?? '')
    if (isImportRowField(field)) errors[field] ??= issue.message
  }
  return errors
}

/** A preview row as the review page starts editing it: the same values, unchanged. */
export const importRowDraft = (row: ImportedRow): ImportRowDraft => ({
  symbol: row.symbol,
  kind: row.kind,
  quantity: row.quantity,
  price: row.price,
  date: row.date,
  notes: row.notes,
})

/**
 * A `no_match` row rebuilt as an editable row, once the member picks the right
 * company.
 *
 * ⚠️ The kind is a GUESS. `needs_detail` carries no `kind`, `price` or `notes`
 * (see BACKEND CHANGES NEEDED), so the row defaults to `SECONDARY`, the
 * serializer's own default — and a sale in the file would come back as a buy.
 * A screen offering this must show the kind and let the member change it.
 */
export const importRowDraftFromNeedsDetail = (row: NeedsDetailRow): ImportRowDraft => ({
  symbol: row.symbol,
  kind: 'SECONDARY',
  quantity: row.quantity,
  price: null,
  date: row.date,
  notes: '',
})

export type AllocationSlice = z.infer<typeof allocationSliceSchema>
export type AnalyticsReport = z.infer<typeof analyticsReportSchema>
export type BonusDraft = z.infer<typeof bonusDraftSchema>
export type BonusRow = z.infer<typeof bonusRowSchema>
export type BuyDraft = z.infer<typeof buyDraftSchema>
export type BuyKind = z.infer<typeof buyKindSchema>
export type BuyReceipt = z.infer<typeof buyReceiptSchema>
export type BuyRow = z.infer<typeof buyRowSchema>
export type CashDividendDraft = z.infer<typeof cashDividendDraftSchema>
export type CashDividendRow = z.infer<typeof cashDividendRowSchema>
export type CompanyMatch = z.infer<typeof companyMatchSchema>
export type Contributor = z.infer<typeof contributorSchema>
export type DeletedProfile = z.infer<typeof deletedProfileSchema>
export type DeletedTransaction = z.infer<typeof deletedTransactionSchema>
export type DisputedRow = z.infer<typeof disputedRowSchema>
export type DividendIncome = z.infer<typeof dividendIncomeSchema>
export type DividendReceipt = z.infer<typeof dividendReceiptSchema>
export type EditableBuyKind = z.infer<typeof editableBuyKindSchema>
export type HealthCheck = z.infer<typeof healthCheckSchema>
export type HealthReport = z.infer<typeof healthReportSchema>
export type HealthStatus = z.infer<typeof healthStatusSchema>
export type Holding = z.infer<typeof holdingSchema>
export type ImportDraft = z.infer<typeof importDraftSchema>
export type ImportPreview = z.infer<typeof importPreviewSchema>
export type ImportResult = z.infer<typeof importResultSchema>
export type ImportRowKind = z.infer<typeof importRowKindSchema>
/** A row that passed `importSaveRowSchema`: what the JSON save actually sends. */
export type ImportSaveRow = z.infer<typeof importSaveRowSchema>
export type ImportedRow = z.infer<typeof importedRowSchema>
export type IncomeKind = z.infer<typeof incomeKindSchema>
export type IncomeReport = z.infer<typeof incomeReportSchema>
export type NeedsDetailRow = z.infer<typeof needsDetailRowSchema>
export type NeedsDetailStatus = z.infer<typeof needsDetailStatusSchema>
export type PortfolioOverview = z.infer<typeof portfolioOverviewSchema>
export type PortfolioSummary = z.infer<typeof portfolioSummarySchema>
export type PriceStatus = z.infer<typeof priceStatusSchema>
export type ProfileCreateDraft = z.infer<typeof profileCreateDraftSchema>
export type ProfileUpdateDraft = z.infer<typeof profileUpdateDraftSchema>
export type ProfitLossReport = z.infer<typeof profitLossReportSchema>
export type ProfitLossRow = z.infer<typeof profitLossRowSchema>
export type ReturnMeasures = z.infer<typeof returnMeasuresSchema>
export type ReturnBreakdown = z.infer<typeof returnBreakdownSchema>
export type SectorContribution = z.infer<typeof sectorContributionSchema>
export type SellDraft = z.infer<typeof sellDraftSchema>
export type SellProceeds = z.infer<typeof sellProceedsSchema>
export type SellReceipt = z.infer<typeof sellReceiptSchema>
export type SellRow = z.infer<typeof sellRowSchema>
export type ShareholderProfile = z.infer<typeof shareholderProfileSchema>
export type ShareholderType = z.infer<typeof shareholderTypeSchema>
export type SkippedRow = z.infer<typeof skippedRowSchema>
export type SnapshotPoint = z.infer<typeof snapshotPointSchema>
export type TradeCosts = z.infer<typeof tradeCostsSchema>
export type TransactionEditDraft = z.infer<typeof transactionEditDraftSchema>
export type TransactionGroup = z.infer<typeof transactionGroupSchema>
export type TransactionHistory = z.infer<typeof transactionHistorySchema>
export type UncoveredRow = z.infer<typeof uncoveredRowSchema>
export type UpcomingIncome = z.infer<typeof upcomingIncomeSchema>
export type UpdatedTransaction = z.infer<typeof updatedTransactionSchema>
export type ValueHistoryPeriod = z.infer<typeof valueHistoryPeriodSchema>
